import React, { useState } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { getFrontMenuItems } from "adminApi/FrontMenuSettingApi/FrontMenuSetting";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import feilds from "../../json/MenuLisrFeilds.json";
import { FilterStatus } from "utils/FilterStatus";

/**
 * MenuListTable Component
 * Renders the list of front-menus. Status is displayed as view-only badges.
 */
const MenuListTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);

  // Role-based access control for the action column
  const canShowAction = useRoleVerify("", ["can_edit"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = feilds;

  // Fetch paginated menu data
  const { data, meta, loading, error, refetch } = usePaginatedFetch(getFrontMenuItems, {
    page,
    limit,
    search: debouncedSearch,
    title: "Front Menu List",
  });

  // Transform raw data (e.g., status 1/0) into UI-friendly badges
  const filterdata = FilterStatus(data);

  // Auto-generate display fields from JSON definition
  const displayFields = generateDisplayFields(tabledata);

  // Hook to generate standard table configuration
  const { formattedData, columns } = useTableConfig({
    data: filterdata,
    page,
    limit,
    canShowAction,
    refetch,
    IsDelete: false, // Deletion restricted per standard frontend policy
    config: {
      entityName: "Menu",
      editUrl: "menu-edit",
      idKey: "menuid",
      displayFields,
    },
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => setPage(p);

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={formattedData}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
        copy: true,
        csv: true,
        pdf: true,
        print: true,
        fileName: "Front Menu List",
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default MenuListTable;
